/**
 * Lip sync (2026-10-06): the mouth shapes Luma makes while she speaks, timed from her voice
 * model. Kokoro reads the text as phonemes, one character per sound, and predicts how many
 * 25 ms frames each lasts; the server sends these cues with the audio and the headset plays
 * them on her face in step with the sound.
 *
 * The shapes are the 15 standard speech visemes (silence, PP, FF, TH, DD, kk, CH, SS, nn,
 * RR, aa, E, ih, oh, ou), which any face rig can be mapped onto.
 */
export const VISEMES = [
  'sil',
  'PP',
  'FF',
  'TH',
  'DD',
  'kk',
  'CH',
  'SS',
  'nn',
  'RR',
  'aa',
  'E',
  'ih',
  'oh',
  'ou',
] as const;
export type Viseme = (typeof VISEMES)[number];

/** A mouth shape and when it starts, in seconds from the start of the audio. */
export interface VisemeCue {
  v: Viseme;
  t: number;
}

const GROUPS: [Viseme, string][] = [
  ['PP', 'pbmɱ'],
  ['FF', 'fv'],
  ['TH', 'θð'],
  ['DD', 'tdɾʔ'],
  ['kk', 'kgɡxŋq'],
  ['CH', 'ʃʒʧʤɕʑ'],
  ['SS', 'sz'],
  ['nn', 'nlɫɲ'],
  ['RR', 'ɹrɻɚɝ'],
  ['aa', 'ɑaæʌɐɒIW'],
  ['E', 'ɛeəɜᵊᵻœA'],
  ['ih', 'ɪiyjʏ'],
  ['oh', 'ɔoøOY'],
  ['ou', 'ʊuwɯ'],
  ['sil', '.,!?;:—–…$"()'],
];
const VISEME_OF = new Map<string, Viseme>();
for (const [v, chars] of GROUPS) for (const c of chars) VISEME_OF.set(c, v);

/** Stress marks come before the vowel they stress: their time belongs to it. */
const BEFORE = new Set(['ˈ', 'ˌ']);

/**
 * The viseme for one phoneme character, or undefined for marks that only lengthen or link
 * (length ː, a word gap, an aspirated h): those extend the shape already showing.
 */
export function visemeOf(ch: string): Viseme | undefined {
  return VISEME_OF.get(ch);
}

/**
 * Timed mouth shapes from the phoneme characters and each one's length in frames. Repeats
 * merge, stress marks give their time to the sound after them, and anything without a shape
 * extends the one before.
 */
export function visemeCues(
  tokens: readonly string[],
  frames: readonly number[],
  secondsPerFrame = 0.025,
): VisemeCue[] {
  const cues: VisemeCue[] = [];
  let t = 0;
  let pending: number | undefined;
  for (let i = 0; i < tokens.length; i++) {
    const ch = tokens[i]!;
    const length = (frames[i] ?? 0) * secondsPerFrame;
    if (BEFORE.has(ch)) {
      pending ??= t;
      t += length;
      continue;
    }
    const v = visemeOf(ch);
    const start = pending ?? t;
    pending = undefined;
    t += length;
    if (!v) continue;
    if (cues.length && cues[cues.length - 1]!.v === v) continue;
    cues.push({ v, t: round(start) });
  }
  // The audio ends closed.
  if (cues.length && cues[cues.length - 1]!.v !== 'sil') cues.push({ v: 'sil', t: round(t) });
  return cues;
}

/** Cues on the wire: `[viseme index, start in ms]`, compact enough for a response header. */
export function encodeCues(cues: readonly VisemeCue[]): [number, number][] {
  return cues.map((c) => [VISEMES.indexOf(c.v), Math.round(c.t * 1000)]);
}

export function decodeCues(wire: unknown): VisemeCue[] {
  if (!Array.isArray(wire)) return [];
  const out: VisemeCue[] = [];
  for (const item of wire) {
    if (!Array.isArray(item) || item.length !== 2) continue;
    const [i, ms] = item as [unknown, unknown];
    const v = typeof i === 'number' ? VISEMES[i] : undefined;
    if (v && typeof ms === 'number' && ms >= 0) out.push({ v, t: ms / 1000 });
  }
  return out;
}

function round(s: number): number {
  return Math.round(s * 1000) / 1000;
}
